import React, {
  useState,
  useCallback,
  useReducer,
  useContext,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { OrganisationContext, UserContext } from "../../layouts/MainLayout";
import moment from "moment-timezone";

/**Services**/
import {
  createScheduleTemplate,
  editScheduleTemplate,
  validateScheduleTemplateName,
} from "../../services/SchedulerService";
/**Componenets**/
import AddJob from "./AddJob";
import Icons from "../../utils/components/icons";
import Modal from "../../utils/components/common/modal";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";
import { getTaskFormattedTime } from "../../utils/utils";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import LoadTemplate from "./LoadTemplate";

const formReducer = (state, action) => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.fieldName]: action.value };
    case "ADD_TASK":
      if (state.jobList.length < 6) {
        return {
          ...state,
          jobList: [...state.jobList, action.value],
        };
      }
      return state;
    case "UPDATE_TASK":
      return {
        ...state,
        jobList: state.jobList.map((job, index) =>
          index === action.index
            ? {
                ...job,
                jobTime: action.jobTime,
                targetMode: action.targetMode,
                edited: true,
              }
            : job
        ),
      };
    case "DELETE_JOB":
      return {
        ...state,
        jobList: state.jobList.filter((_, index) => index !== action.index),
      };
    case "UPDATE_ALL_JOBS":
      return {
        ...state,
        jobList: action.value,
      };
    default:
      return state;
  }
};

const taskHeadings = ["editedIcon", "task", "when", "action", "actionsOptions"];

const AddSchedules = ({
  onClose,
  selectedSchdData,
  schdActionType = "add",
}) => {
  const { t } = useTranslation("schedules");
  const { userCognitoData } = useContext(UserContext);
  const { siteData } = useContext(OrganisationContext);

  const initialState = useMemo(() => {
    if (schdActionType === "edit") {
      const jobList = selectedSchdData?.jobList.map((job) => {
        const parts = job.jobTime.split(" ");
        if (parts.length >= 6) {
          parts.shift();
        }
        const jobTime = parts.join(" ");
        return {
          jobTime: jobTime,
          targetMode: job.targetMode,
          edited: false,
        };
      });
      let name = selectedSchdData?.scheduleTemplateName;
      return {
        step: 1,
        scheduleName: name,
        scheduleDescription: selectedSchdData?.scheduleTemplateDescription,
        jobList: jobList,
      };
    }
    return {
      step: 1,
      scheduleName: "",
      scheduleDescription: "",
      jobList: [],
    };
  }, [schdActionType, selectedSchdData]);

  const [isModalOpen, setIsModalOpen] = useState(true);
  const [openAddJobModal, setOpenAddJobModal] = useState(false);
  const [openLoadTemplateModal, setOpenLoadTemplateModal] = useState(false);
  const [jobToEdit, setJobToEdit] = useState();
  const [currentTab, setCurrentTab] = useState(1);
  const [formState, formDispatch] = useReducer(formReducer, initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [isInlineLoader, setIsInlineLoader] = useState(false);
  const [editSaveClicked, setEditSaveClicked] = useState(true); // to show inline loader on Save or saveAs.
  const [notUniqueErr, setNotUniqueErr] = useState(false); // name not unique for SaveAs option.
  const [showRunTimeErr, setShowRunTimeErr] = useState(false); // show Runtime error in name/desc after clicking next for first time.
  const [hideParentPopup, setHideParentPopup] = useState(false); // To show saveAs error popup instead of Add Schedule Modal

  /**Information and confirm modal states: */
  const [infoModal, setInfoModal] = useState({
    open: false,
    msg: "",
    redirect: false,
  });
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    msg: "",
    type: "",
    data: {},
  });

  const isFormValid = useMemo(() => {
    /*Name is already validated on next click so only job list check is needed as of now.*/
    return !!formState.jobList.length;
  }, [formState]);

  const modesList = {
    OPERATIONAL: t("changeModesTo.operational"),
    SENSE_ONLY: t("changeModesTo.senseOnly"),
    STANDBY: t("changeModesTo.standBy"),
    RESEARCH: t("changeModesTo.research"),
  };

  const [formValid, setFormValid] = useState({
    isStepOneValid: false,
    isStepTwoValid: false,
    isNameValid: true,
    isDescValid: true,
    isValid: false, //For only spaces case
    job: "",
  });

  /**Function to validate is there any kind of change in Schd Name/Desc/Job to enable/disable Save/SaveAs button in Edit Schedule **/
  const isScheduleDataUpdated = useCallback(
    (currentSchData, initialSchData) => {
      /**Check for change in Schedule name or description **/
      if (
        currentSchData?.scheduleName?.trim() !==
          initialSchData?.scheduleTemplateName?.trim() ||
        currentSchData?.scheduleDescription?.trim() !==
          initialSchData?.scheduleTemplateDescription?.trim()
      ) {
        return true;
      }

      /**Check for change in schdule job(task) list**/

      /**Check for added or deleted jobs by comparing array length on new list and old list*/
      if (currentSchData.jobList.length !== initialSchData.jobList.length) {
        return true; // Number of jobs changed
      }

      // Check for updated jobs
      for (let i = 0; i < currentSchData.jobList.length; i++) {
        const currentJob = currentSchData.jobList[i];
        const initialJob = initialSchData.jobList[i];

        // Check if jobTime or targetMode has changed [appending 00 as from backend we are getting 00 appended in jobTime]
        if (
          `00 ${currentJob.jobTime}` !== initialJob.jobTime ||
          currentJob.targetMode !== initialJob.targetMode
        ) {
          return true; // Job updated
        }
      }

      return false; // No changes
    },
    []
  );

  const isEditSaveEnable = useMemo(() => {
    const isEnable = { saveBtn: true, saveAsBtn: true };
    if (currentTab === 2) {
      const isDataChanged = isScheduleDataUpdated(formState, selectedSchdData);
      if (!isFormValid || !isDataChanged) {
        /**If form is invalid or there is no change in the form data then disable both Save and SaveAs buttons**/
        isEnable.saveBtn = false;
        isEnable.saveAsBtn = false;
      }
    }
    return isEnable;
  }, [
    isScheduleDataUpdated,
    currentTab,
    formState,
    isFormValid,
    selectedSchdData,
  ]);

  const onCancelClick = useCallback(() => {
    /**Open confirmation modal onclick of cancel */
    setConfirmModal((prev) => ({
      ...prev,
      open: true,
      msg:
        schdActionType === "add"
          ? t("addCancelConfirmMsg")
          : t("editCancelConfirmMsg"),
      type: "cancel",
    }));
  }, [schdActionType, t]);

  const onDeleteJobClick = useCallback(
    (index) => {
      /**Open confirmation modal onclick of delete */
      setConfirmModal((prev) => ({
        ...prev,
        open: true,
        msg: t("deleteTaskConfirmMsg"),
        type: "deleteJob",
        data: { selectedIndex: index },
      }));
    },
    [t]
  );

  const onInfoModalClose = useCallback(() => {
    setInfoModal((prev) => {
      if (prev.redirect) {
        setCurrentTab(1);
        setHideParentPopup(false);
        setNotUniqueErr(true);
      }
      return { ...prev, open: false, msg: "", redirect: false };
    });
  }, []);

  const onConfirmModalClose = useCallback(() => {
    //Reset on modal close
    setConfirmModal((prev) => ({
      ...prev,
      open: false,
      msg: "",
      type: "",
    }));
  }, []);

  const onConfirm = useCallback(
    (type) => {
      if (type === "cancel") {
        setConfirmModal((prev) => ({
          ...prev,
          open: false,
          msg: "",
          type: "",
        }));
        setIsModalOpen(false);
        onClose(null);
      }

      if (type === "deleteJob") {
        /**Delete job logic */
        formDispatch({
          type: "DELETE_JOB",
          index: confirmModal.data.selectedIndex,
        });
        setConfirmModal((prev) => ({
          ...prev,
          open: false,
          msg: "",
          type: "",
          data: {},
        }));
      }
    },
    [confirmModal.data.selectedIndex, onClose]
  );

  const onAddJobClick = useCallback(() => {
    setOpenAddJobModal(true);
  }, []);

  const onLoadTemplateClick = useCallback(() => {
    setOpenLoadTemplateModal(true);
  }, []);

  const onEditJobClick = useCallback(
    (index) => {
      const jobDetails = { ...formState.jobList[index] };
      jobDetails.index = index;
      setJobToEdit(jobDetails);
      setOpenAddJobModal(true);
    },
    [formState]
  );

  const onBackClick = useCallback(() => {
    setCurrentTab(1);
  }, []);

  const onNextClick = useCallback(async () => {
    setShowRunTimeErr(true);
    setNotUniqueErr(false);
    if (!formValid.isNameValid || !formValid.isDescValid) {
      return;
    }
    if (
      schdActionType === "edit" &&
      selectedSchdData?.scheduleTemplateName === formState?.scheduleName
    ) {
      /**If schdActionType is edit & there is no change in name then redirect user to tab-2
       * else for any name change in edit mode or for Add action, execute validateScheduleTemplateName API.
       */
      setCurrentTab(2);
      setShowRunTimeErr(false);
      return;
    }
    setIsInlineLoader(true);
    setIsLoading(true);

    // no unique name validation on next tab click action for edit schedule.
    try {
      await validateScheduleTemplateName({
        siteId: siteData.id,
        scheduleTemplateName: formState.scheduleName,
      });
      setCurrentTab(2);
      setShowRunTimeErr(false);
    } catch (err) {
      if (err?.response?.data?.statusCode === "NAME_ALREADY_EXISTS") {
        setInfoModal((prev) => ({
          ...prev,
          open: true,
          msg: t("notUniqueName"),
        }));
      } else {
        setInfoModal((prev) => ({
          ...prev,
          open: true,
          msg: t("somethingWentWrong"),
        }));
      }
    }
    setIsInlineLoader(false);

    setIsLoading(false);
  }, [
    formState,
    formValid,
    schdActionType,
    selectedSchdData?.scheduleTemplateName,
    siteData.id,
    t,
  ]);

  /**Handle name/description change to update formData**/
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "scheduleName") {
      let isValid = false;
      if (value.trim().length) {
        isValid = /^[a-zA-Z0-9_\-: ]{1,16}$/.test(value);
      }
      setFormValid((valid) => ({ ...valid, isNameValid: isValid }));
    }
    if (name === "scheduleDescription") {
      const isValid = /^(?:[a-zA-Z0-9_\-: ,]{1,128})?$/.test(value);
      setFormValid((valid) => ({ ...valid, isDescValid: isValid }));
    }
    formDispatch({ type: "SET_FIELD", fieldName: name, value });
  }, []);

  const onAddJobModalClose = useCallback((value) => {
    setJobToEdit();
    setOpenAddJobModal(false);
    if (value) {
      value.addEditType === "ADD"
        ? formDispatch({ type: "ADD_TASK", value: value })
        : formDispatch({
            type: "UPDATE_TASK",
            jobTime: value.jobTime,
            targetMode: value.targetMode,
            index: value.index,
            jobEdited: true,
          });
    }
  }, []);

  const onLoadTemplateModalClose = useCallback(() => {
    setOpenLoadTemplateModal(false);
  }, []);

  /**Submit Action*/
  const onAddScheduleClick = useCallback(async () => {
    // setIsInlineLoader(true);
    setIsLoading(true);
    const jobDetails = formState.jobList.map((job) => {
      return { ...job, jobTime: `00 ${job.jobTime}` };
    });
    const localTimeZone = moment.tz.guess();
    const param = {
      requestedUserId:
        userCognitoData?.signInUserSession?.accessToken?.payload?.username,
      siteId: siteData?.id,
      scheduleTemplateName: formState?.scheduleName,
      scheduleTemplateDescription: formState?.scheduleDescription,
      // scheduleDurationType: "WEEKLY",
      scheduleType: "CHANGE_MODE",
      jobList: jobDetails,
      userTimezone: localTimeZone,
    };
    try {
      await createScheduleTemplate(param);
      setIsInlineLoader(false);
      setIsLoading(false);
      onClose(
        schdActionType === "edit" ? "schdSavedAs" : "schdCreated",
        formState.scheduleName
      );
    } catch (err) {
      setIsInlineLoader(false);
      setIsLoading(false);
      if (
        schdActionType === "edit" &&
        err?.response?.data?.statusCode === "NAME_ALREADY_EXISTS"
      ) {
        setHideParentPopup(true);
        setInfoModal((prev) => ({
          ...prev,
          open: true,
          msg: t("notUniqueNameForSaveAs"),
          redirect: true,
        }));
        return;
      }
      setInfoModal((prev) => ({
        ...prev,
        open: true,
        msg:
          schdActionType === "edit"
            ? t("schdNotEditedErr")
            : t("schdNotCreatedErr"),
      }));
    }
  }, [formState, userCognitoData, siteData, t, onClose, schdActionType]);

  const onEditSaveScheduleClick = useCallback(async () => {
    setIsLoading(true);
    const jobDetails = formState.jobList.map((job) => {
      return { ...job, jobTime: `00 ${job.jobTime}` };
    });
    const localTimeZone = moment.tz.guess();
    const param = {
      requestedUserId:
        userCognitoData?.signInUserSession?.accessToken?.payload?.username,
      scheduleTemplateId: selectedSchdData.PK.slice(4),
      scheduleTemplateName: formState?.scheduleName,
      scheduleTemplateDescription: formState?.scheduleDescription,
      // scheduleDurationType: "WEEKLY",
      scheduleType: "CHANGE_MODE",
      jobList: jobDetails,
      userTimezone: localTimeZone,
    };
    try {
      await editScheduleTemplate(param);
      setIsLoading(false);
      onClose("schdSaved");
    } catch (err) {
      setIsLoading(false);
      setInfoModal((prev) => ({
        ...prev,
        open: true,
        msg: t("schdNotEditedErr"),
      }));
    }
  }, [formState, userCognitoData, selectedSchdData, onClose, t]);

  const loadJobFromTemplate = (template) => {
    const jobList = template.jobList.map((job, i) => {
      const parts = job.jobTime.split(" ");
      if (parts.length >= 6) {
        parts.shift();
      }
      const jobTime = parts.join(" ");
      return {
        jobTime: jobTime,
        targetMode: job.targetMode,
        edited: false,
        index: i,
      };
    });
    formDispatch({
      type: "UPDATE_ALL_JOBS",
      value: jobList,
    });
  };

  return (
    <div className="add-schedule-container">
      <Modal
        isOpen={isModalOpen}
        idName="add-schedule-modal"
        className={`${
          openAddJobModal || openLoadTemplateModal || hideParentPopup
            ? "add-schedule-modal hide"
            : "add-schedule-modal"
        }`}
      >
        {/* Header Starts */}
        <div className="schd-header">
          <p className="heading" id="schd-heading">
            {schdActionType === "add" ? t("addTemplate") : t("editTemplate")}
          </p>
        </div>
        {/* Header Ends */}

        {/* Body Container Starts */}
        <div className="schd-body-container">
          <div className="tabs">
            <div
              id="name-and-desc-tab"
              className={
                currentTab === 1 ? "active font-22 tab1" : "font-22 tab1"
              }
            >
              {t("nameAndDescription")}
            </div>
            <div
              id="task-tab"
              className={
                currentTab === 2 ? "active font-22 tab2" : "font-22 tab2"
              }
            >
              {t("tasks")}
            </div>
          </div>
          {/* Tab wise content starts */}
          <div className="tabs-content">
            {currentTab === 1 ? (
              <div className="tab-1">
                {/* Name Field */}
                <div className="row-content test">
                  <label id="scheduleName_label">{t("scheduleName")}</label>
                  <input
                    type="text"
                    className="form-control"
                    id="scheduleName"
                    placeholder={t("scheduleNamePlaceholder")}
                    maxLength="16"
                    name="scheduleName"
                    value={formState.scheduleName}
                    onChange={handleChange}
                  />
                  <>
                    {showRunTimeErr &&
                      formState.scheduleName &&
                      !formValid.isNameValid && (
                        <span className="error text-h4 schNameErr" id="schNameErr">
                          {t("schNameErr")}
                        </span>
                      )}
                    {notUniqueErr && !showRunTimeErr && (
                      <span className="error text-h4 schNameErr" id="schNameErr">
                        {t("notUniqueNameErrForSaveAs")}
                      </span>
                    )}
                  </>
                </div>
                {/* Description Field */}
                <div className="row-content">
                  <div className="d-flex justify-content-between">
                    <label id="scheduleDescription_label">
                      {t("scheduleDescription")}{" "}
                      <label className="secondary-text">{t("optional")}</label>
                    </label>
                    <label className="secondary-text" id="max-char-limit">
                      <div>{t("max128")}</div>
                    </label>
                  </div>
                  <textarea
                    rows="4"
                    className="form-control"
                    id="scheduleDescription"
                    placeholder={t("scheduleDescriptionPlaceholder")}
                    maxLength="128"
                    name="scheduleDescription"
                    value={formState.scheduleDescription}
                    onChange={handleChange}
                  />
                  {showRunTimeErr && !formValid.isDescValid && (
                    <div className="error text-h4 schDescErr" id="schDescErr">
                      {t("schDescErr")}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="tab-2">
                <div className="job-action-container">
                  <div className="load-template" onClick={onLoadTemplateClick}>
                    {Icons.loadTemplate}{" "}
                    <label
                      className="load-template-link-label"
                      id="load-template-link-label"
                      title={t("loadTemplateText")}
                    >
                      {t("loadTemplateText")}
                    </label>
                  </div>
                  <div
                    className={
                      formState.jobList.length < 6
                        ? "add-job-option"
                        : "add-job-option disabled"
                    }
                    onClick={
                      formState.jobList.length < 6 ? onAddJobClick : null
                    }
                  >
                    {Icons.addATask}{" "}
                    <label className="add_job_link_label" title={t("addTask")} id="add_job_link_label">
                      {t("addTask")}
                    </label>
                  </div>
                </div>

                <div className="job-list">
                  <table className="table job-table" id="tasks-table">
                    <thead>
                      <tr>
                        {taskHeadings.map((heading, i) => (
                          <td key={heading} className={`${heading} col-${i}`} id={heading} name={heading}>
                            {t(`taskHeadings.${heading}`)}
                          </td>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {formState.jobList.length ? (
                        formState.jobList.map((job, i) => {
                          return (
                            <tr
                              key={job.jobTime}
                              className={job?.edited ? "edited-job-row" : ""}
                            >
                              <td className="edit-icon" id="edit-icon">{job?.edited ? Icons.editedJob : ""}</td>
                              <td id="task-number" className="task-number">{`${i + 1}`}</td>
                              <td id="scheduled-time" className="scheduled-time">
                                {getTaskFormattedTime(
                                  job.jobTime,
                                  formState.schedulerRecurType
                                )}
                              </td>
                              <td id="action-type" className="action-type">{modesList[job.targetMode]}</td>
                              <td id="action-icons" className="action-icons">
                                <span
                                  className="edit-action"
                                  id="edit-action"
                                  name="edit-action"
                                  title={t("editTaskTooltip")}
                                  onClick={() => {
                                    onEditJobClick(i);
                                  }}
                                >
                                  {Icons.editTask}
                                </span>
                                <span
                                  className="delete-action"
                                  id="delete-action"
                                  name="delete-action"
                                  title={t("deleteTaskTooltip")}
                                  onClick={() => {
                                    onDeleteJobClick(i);
                                  }}
                                >
                                  {Icons.deleteJob}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="no-job-container">
                            {t("noTasksMsg")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          {/* Tab wise content ends */}
        </div>
        {/* Body Container Ends */}

        {/* Footer Starts */}
        <div className="schd-footer">
          {currentTab === 2 ? (
            <div
              className="back-btn"
              id="back-btn"
              onClick={onBackClick}
              title={t("btn.back")}
            >
              {Icons.leftArrowIcon}
            </div>
          ) : (
            <span></span>
          )}
          <div className="right">
            <button
              id="cancel"
              className="roundBtn dark"
              onClick={onCancelClick}
              title={t("btn.cancel")}
            >
              <span>{t("btn.cancel")}</span>
            </button>
            {currentTab === 1 && (
              <button
                id="next"
                className="roundBtn primary"
                disabled={
                  !formState.scheduleName ||
                  (showRunTimeErr &&
                    !(formValid.isNameValid && formValid.isDescValid))
                }
                onClick={onNextClick}
              >
                <span>{t("btn.next")}</span>
              </button>
            )}
            {currentTab === 2 && schdActionType !== "edit" && (
              <button
                id="add"
                className="roundBtn primary"
                disabled={!isFormValid}
                onClick={onAddScheduleClick}
              >
                <span>{t("btn.add")}</span>
              </button>
            )}

            {currentTab === 2 && schdActionType === "edit" && (
              <>
                <button
                  id="edit-save"
                  className="roundBtn primary"
                  disabled={!isEditSaveEnable.saveBtn}
                  onClick={() => {
                    setEditSaveClicked(true);
                    onEditSaveScheduleClick();
                  }}
                  title={
                    selectedSchdData?.AssociatedDeviceList?.length
                      ? t("editSaveTitleOnDisable")
                      : isEditSaveEnable.saveBtn
                      ? t("editSaveTitleOnEnable")
                      : ""
                  }
                >
                  {isInlineLoader && editSaveClicked ? (
                    <span>
                      {t("btn.save")}...
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      ></div>
                    </span>
                  ) : (
                    <span>{t("btn.save")} </span>
                  )}
                </button>
                <button
                  id="edit-save-as"
                  className="roundBtn primary"
                  disabled={!isEditSaveEnable.saveAsBtn}
                  onClick={() => {
                    setEditSaveClicked(false);
                    onAddScheduleClick();
                  }}
                  title={!isEditSaveEnable.saveAsBtn ? "" : t("saveAsTitle")}
                >
                  {isInlineLoader && !editSaveClicked ? (
                    <span>
                      {t("btn.saveAs")}...
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      ></div>
                    </span>
                  ) : (
                    <span>{t("btn.saveAs")}</span>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
        {/* Footer Ends */}
      </Modal>
      {/* Body Section ends */}
      {isLoading && <LoadingSpinner />}

      {/* Add Job Component */}
      {openAddJobModal && (
        <AddJob
          onAddJobModalClose={onAddJobModalClose}
          existingJobs={[...(formState.jobList.filter(job=>job?.jobTime!==jobToEdit?.jobTime))]}  //remove the job which we are editing
          jobToEdit={jobToEdit}
        />
      )}

      {/* Load Template Component */}
      {openLoadTemplateModal && (
        <LoadTemplate
          onLoadTemplateModalClose={onLoadTemplateModalClose}
          currentSelectedTemplate={(template) => {
            loadJobFromTemplate(template);
            // formDispatch({
            //   type: "UPDATE_TASK",
            //   jobTime: value.jobTime,
            //   targetMode: value.targetMode,
            //   index: value.index,
            //   jobEdited: true,
            // });
          }}
        />
      )}

      {/* Err/Success Modal */}
      <ModalComponent
        idName="schedule-info-modal"
        isOpen={infoModal.open}
        onCloseModal={onInfoModalClose}
        descriptionText={infoModal.msg}
      />

      {/* Comfirmation Modal */}
      <ModalComponent
        idName="schedule-confirm-modal"
        isOpen={confirmModal.open}
        onCloseModal={onConfirmModalClose}
        descriptionText={confirmModal.msg}
        needConfirmation={true}
        onConfirm={() => {
          onConfirm(confirmModal.type);
        }}
      />
    </div>
  );
};

export default AddSchedules;
